import {
  HelpCenterProvider
} from "./chunk-WLDRGDKP.mjs";
import {
  Sidebar
} from "./chunk-AVBRTOMH.mjs";
import {
  SidePanel
} from "./chunk-A2UNT4DX.mjs";
import {
  LayoutContainer
} from "./chunk-FU7O6WQF.mjs";
import {
  Footer
} from "./chunk-GGKPFDRF.mjs";
import {
  LayoutProvider
} from "./chunk-HFINPXUM.mjs";
import {
  menu_items_default
} from "./chunk-GEOI2MCG.mjs";

// src/layout.tsx
import * as React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Appcues } from "@clara/appcues";
import {
  TwoFactorActivation,
  useAuthContext,
  useMultientityContext
} from "@clara/auth";
import { CustomerIo } from "@clara/customer-io";
import { useLocaleContext, useT } from "@clara/locale";
import { useNotifications } from "@clara/notifications";
import { Qualtrics } from "@clara/qualtrics";
import { Flex } from "@clara/ui";
import { identity, isEmpty, isFunction, pick, __DEV__ } from "@clara/utils";
import { jsx, jsxs } from "react/jsx-runtime";
function Layout(props) {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addMenuItem = identity,
    onItemSelect,
    children,
    beamerOptions,
    variant: variantProp,
    activePredicate = (item) => {
      var _a;
      return (_a = window.location.pathname) == null ? void 0 : _a.startsWith(String(item == null ? void 0 : item.id));
    },
    customerIoSiteId,
    appcuesId,
    zendeskHost,
    ...rest
  } = props;
  const { signOut, ability, user, is_candidate, config } = useAuthContext();
  const { lang, locale } = useLocaleContext();
  const { has_multientities } = useMultientityContext();
  const notifications = useNotifications();
  const { t } = useT("common");
  const location = useLocation();
  const navigate = useNavigate();
  const countryISO = locale.preferences.id;
  const variant = variantProp || is_candidate ? "candidate" : "customer";
  const beamer = {
    product_id: beamerOptions == null ? void 0 : beamerOptions.product_id,
    user_id: user == null ? void 0 : user.uuid,
    user_email: user == null ? void 0 : user.email,
    user_firstname: user == null ? void 0 : user.firstName,
    user_lastname: user == null ? void 0 : user.paternalSurname,
    filter: user == null ? void 0 : user.role,
    language: lang.split("-")[0].toUpperCase()
  };
  const qualtrics = {
    userUUID: user == null ? void 0 : user.uuid,
    projectId: props == null ? void 0 : props.qualtricsProjectId
  };
  const customerIOProps = {
    user,
    siteId: customerIoSiteId
  };
  const appcuesProps = {
    appcuesId,
    user: pick(user != null ? user : {}, [
      "uuid",
      "email",
      "firstName",
      "paternalSurname",
      "role",
      "companyUuid"
    ])
  };
  const isSidebarVisible = variant === "customer";
  const handleChange = (event, item) => {
    event.preventDefault();
    if (notifications.state.value)
      notifications.addVisitedCampaign(item, notifications.state.value);
    if (isFunction(onItemSelect)) {
      return onItemSelect(item);
    }
    if (config.unstable_Redirect && item.id) {
      return navigate(item.id);
    }
    if (__DEV__) {
      console.warn(`You are invoking in '__DEV__' environment, this is a no-op`);
      return;
    }
    if (item.id)
      window.location.assign(item.id);
  };
  const items = React.useMemo(() => {
    if (isEmpty(user))
      return null;
    return menu_items_default(ability, user, countryISO);
  }, [ability, user, countryISO]);
  const highlightPred = (item, campaigns) => {
    if (item.isPro)
      return true;
    if (!(Array.isArray(campaigns) && campaigns.length > 0))
      return false;
    return campaigns.some((campaign) => {
      var _a;
      return campaign.key === item.id || ((_a = item.subNav) == null ? void 0 : _a.some((subItem) => subItem.id === campaign.key));
    });
  };
  const proFeatures = [];
  return /* @__PURE__ */ jsx(
    LayoutProvider,
    {
      beamerOptions: beamer,
      variant,
      hasMultientities: has_multientities,
      ...rest,
      children: /* @__PURE__ */ jsxs(HelpCenterProvider, { zendeskHost, children: [
        /* @__PURE__ */ jsx(Qualtrics, { ...qualtrics }),
        isSidebarVisible && /* @__PURE__ */ jsx(
          Sidebar,
          {
            activePredicate: (item) => {
              var _a;
              if (config.unstable_Redirect)
                return (_a = location.pathname) == null ? void 0 : _a.startsWith(item == null ? void 0 : item.id);
              return activePredicate(item, item.id);
            },
            items,
            mapItem: (item) => {
              const next = {
                ...item,
                isPro: proFeatures.some((id) => {
                  var _a;
                  return item.id === id || ((_a = item.subNav) == null ? void 0 : _a.some((subItem) => subItem.id === id));
                })
              };
              return {
                ...next,
                title: t(`sidebar.${item.title}`),
                isHighlighted: highlightPred(next, notifications.state.value),
                highlightLabel: next.isPro ? "Pro" : t("new"),
                highlightColor: next.isPro ? "pro" : "advice"
              };
            },
            onChange: handleChange,
            onSignOutClick: signOut,
            signOutLabel: t("sidebar.signOut")
          }
        ),
        /* @__PURE__ */ jsxs(LayoutContainer, { children: [
          /* @__PURE__ */ jsx(Flex, { flex: 1, direction: "column", children: config.unstable_Redirect ? /* @__PURE__ */ jsxs(React.Fragment, { children: [
            children,
            /* @__PURE__ */ jsx(Flex, { flex: 1, position: "relative", direction: "column", children: /* @__PURE__ */ jsx(Outlet, {}) })
          ] }) : /* @__PURE__ */ jsxs(React.Fragment, { children: [
            children,
            /* @__PURE__ */ jsx(Outlet, {})
          ] }) }),
          /* @__PURE__ */ jsx(Footer, {})
        ] }),
        /* @__PURE__ */ jsx(TwoFactorActivation, { children: /* @__PURE__ */ jsx(SidePanel, { size: (theme) => theme.spacing(60) }) }),
        /* @__PURE__ */ jsx(CustomerIo, { ...customerIOProps }),
        /* @__PURE__ */ jsx(Appcues, { ...appcuesProps })
      ] })
    }
  );
}

export {
  Layout
};
